import initCanvas from './canvas';
import initAccessories from './accessories';
import initUndo from './undo';
import initShareModal from './share';

document.addEventListener('DOMContentLoaded', () => {
	const wrapper = document.querySelector('.wp-block-mavis-design-a-hairstyle');
	if (!wrapper) return;

	const dom = {
		wrapper,
		canvas: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__canvas'),
		accessoryContainer: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__accessory-container',
		),
		startPoint: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__start'),
		hairOption: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-option--hair'),
		colourOption: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-option--colour'),
		accessoriesOption: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__game-option--accessories',
		),
		colourOptionsList: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__game-colour-options',
		),
		colourItems: wrapper.querySelectorAll('.wp-block-mavis-design-a-hairstyle__game-colour-option'),
		accessoryButtons: wrapper.querySelectorAll(
			'.wp-block-mavis-design-a-hairstyle__accessories-list .wp-block-mavis-design-a-hairstyle__accessory img',
		),
		undoButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__undo-button'),
		clearButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__clear-button'),
		startOverButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__start-over-button'),
		shareButton: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__share-button'),
		optionsContainer: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__game-options'),
		headSelection: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__head-selection'),
		headChoices: wrapper.querySelectorAll('.wp-block-mavis-design-a-hairstyle__head-choice'),
		headContainer: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__head-container'),
		pointingHand: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__pointing-hand'),
		tutorialModal: wrapper.querySelector('.wp-block-mavis-design-a-hairstyle__tutorial-modal'),
		tutorialStartButton: wrapper.querySelector(
			'.wp-block-mavis-design-a-hairstyle__tutorial-start-button',
		),
	};

	const state = {
		currentMode: 'drawing',
		drawingColor: '#000000',
		selectedAccessory: null,
	};

	/**
	 * Helper function to display the tutorial.
	 * @param {Function} onComplete - The callback to run after the tutorial is dismissed.
	 */
	const displayTutorial = (onComplete) => {
		if (dom.tutorialModal && dom.tutorialStartButton) {
			dom.tutorialModal.classList.add('is-visible');

			dom.tutorialStartButton.addEventListener(
				'click',
				() => {
					dom.tutorialModal.classList.remove('is-visible');

					if (typeof onComplete === 'function') {
						onComplete();
					}
				},
				{ once: true },
			);
		}
	};

	/**
	 * Adds or removes the custom coloring cursor class from the body
	 * based on the current application mode.
	 */
	const updateCursor = () => {
		if (state.currentMode === 'coloring') {
			dom.canvas.classList.add('coloring-cursor');
		} else {
			dom.canvas.classList.remove('coloring-cursor');
		}
	};

	/**
	 * Resets the UI to the default drawing mode.
	 *
	 * @returns {void}
	 */
	const resetToDrawingMode = () => {
		state.currentMode = 'drawing';
		updateCursor();
		state.selectedAccessory = null;
		dom.hairOption.classList.add('is-active');
		dom.colourOption.classList.remove('is-active');
		dom.accessoriesOption.classList.remove('is-active');
		dom.colourOptionsList.classList.add(
			'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
		);
		dom.accessoryButtons.forEach((b) => b.classList.remove('is-active'));
	};

	/**
	 * Checks if the canvas has any content (drawing or accessories).
	 * @returns {boolean} - True if the canvas has content, false otherwise.
	 */
	const checkCanvasHasContent = () => {
		const ctx = dom.canvas.getContext('2d');
		const hasDrawing = (() => {
			const pixels = ctx.getImageData(0, 0, dom.canvas.width, dom.canvas.height).data;
			for (let i = 0; i < pixels.length; i += 4) {
				if (pixels[i + 3] > 0) return true;
			}
			return false;
		})();

		const hasAccessories = dom.accessoryContainer.querySelector('[data-placed-accessory]') !== null;
		const hasContent = hasDrawing || hasAccessories;

		dom.undoButton?.classList.toggle('is-active', hasContent);
		dom.clearButton?.classList.toggle('is-active', hasContent);
		dom.startOverButton?.classList.toggle('is-active', hasContent);
		dom.shareButton?.classList.toggle('is-active', hasContent);
		dom.optionsContainer?.classList.toggle('is-active', hasContent);

		return hasContent;
	};

	/**
	 * Initializes the game logic after a head has been chosen.
	 */
	const startGame = () => {
		const { pushState } = initUndo({ dom, checkCanvasHasContent, resetToDrawingMode });
		const { deactivateCursorAccessory } = initAccessories({
			dom,
			state,
			checkCanvasHasContent,
			pushState,
		});
		initCanvas({
			dom,
			state,
			checkCanvasHasContent,
			pushState,
			deactivateCursorAccessory,
		});
		initShareModal({ dom, wrapper });

		// Listen for a click on the designated start area to hide the hand.
		if (dom.startPoint && dom.pointingHand) {
			dom.startPoint.addEventListener(
				'pointerdown',
				() => {
					dom.pointingHand.classList.remove('is-visible');
				},
				{ once: true },
			);
		}

		if (dom.startOverButton) {
			dom.startOverButton.addEventListener('click', () => {
				window.location.reload();
			});
		}

		dom.hairOption.addEventListener('click', () => {
			deactivateCursorAccessory();
			resetToDrawingMode();
		});

		dom.colourOption.addEventListener('click', () => {
			deactivateCursorAccessory();
			state.currentMode = 'coloring';
			updateCursor();
			dom.colourOption.classList.add('is-active');
			dom.hairOption.classList.remove('is-active');
			dom.accessoriesOption.classList.remove('is-active');
			dom.colourOptionsList.classList.toggle(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);
		});

		dom.accessoriesOption.addEventListener('click', () => {
			deactivateCursorAccessory();
			state.currentMode = 'accessorizing';
			updateCursor();
			state.selectedAccessory = null;
			dom.accessoriesOption.classList.add('is-active');
			dom.hairOption.classList.remove('is-active');
			dom.colourOption.classList.remove('is-active');
			dom.colourOptionsList.classList.add(
				'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
			);
		});

		dom.colourItems.forEach((item) => {
			item.addEventListener('click', (e) => {
				e.stopPropagation();
				state.drawingColor = item.style.backgroundColor;
				state.currentMode = 'coloring';
				updateCursor();
				dom.colourItems.forEach((c) => c.classList.remove('is-active'));
				item.classList.add('is-active');
				dom.colourOptionsList.classList.add(
					'wp-block-mavis-design-a-hairstyle__game-colour-options--hidden',
				);
			});
		});

		resetToDrawingMode();
	};

	// Head selection logic
	if (dom.headChoices.length > 0) {
		dom.headChoices.forEach((choice) => {
			choice.addEventListener('click', () => {
				const img = choice.querySelector('img');
				if (!img) return;

				dom.headContainer.insertAdjacentHTML(
					'afterbegin',
					`<img src="${img.src}" alt="${img.alt}"
            class="wp-block-mavis-design-a-hairstyle__head-image"
            usemap="#wp-block-mavis-design-a-hairstyle__map">`,
				);

				if (dom.headSelection) {
					dom.headSelection.style.display = 'none';
				}

				/**
				 * Callback function to be executed when the tutorial is complete.
				 */
				const onTutorialComplete = () => {
					if (dom.pointingHand) {
						dom.pointingHand.classList.add('is-visible');
					}
				};
				displayTutorial(onTutorialComplete);

				startGame();
			});
		});
	} else {
		/**
		 * Fallback for when no head choices are available.
		 */
		const singleHeadImage = dom.headContainer.querySelector(
			'.wp-block-mavis-design-a-hairstyle__head-image',
		);

		/**
		 * Callback function to be executed when the tutorial is complete.
		 */
		const onTutorialComplete = () => {
			if (singleHeadImage && dom.pointingHand) {
				dom.pointingHand.classList.add('is-visible');
			}
		};

		displayTutorial(onTutorialComplete);

		startGame();
	}
});
